import type { PortalStrings } from "./en";

export const es: PortalStrings = {
  shell: {
    portal: "Portal del cliente",
    nav: {
      home: "Inicio",
      case: "Mi caso",
      caseShort: "Caso",
      tasks: "Tareas",
      documents: "Documentos",
      profile: "Mi perfil",
      profileShort: "Perfil"
    },
    signOut: "Cerrar sesión",
    language: "Idioma",
    previewing:
      "Está viendo el portal de {{name}} tal como lo ve. Solo lectura: aquí no se guarda ni se envía nada.",
    exitPreview: "Salir de la vista previa",
    privacy: "Solo usted y su equipo legal pueden ver su información.",
    notClient: "Esta cuenta no tiene un portal de cliente",
    notClientBody:
      "Pida a su equipo legal que lo invite, o inicie sesión con el correo al que le enviaron la invitación.",
    useDifferentEmail: "Usar otro correo",
    cantPreview: "No se puede ver una vista previa de este portal",
    loadFailed:
      "No pudimos cargar su portal. Inténtelo de nuevo en un momento.",
    retry: "Intentar de nuevo"
  },
  greeting: {
    morning: "Buenos días, {{name}}",
    afternoon: "Buenas tardes, {{name}}",
    evening: "Buenas noches, {{name}}",
    withTasks_one: "Esto es lo único que necesitamos de usted.",
    withTasks_other: "Esto es lo que necesitamos de usted.",
    stageLine: {
      started: "Su caso está comenzando.",
      gathering: "Estamos reuniendo la información para su caso.",
      review: "Su abogado está revisando su caso.",
      filed: "Su caso fue presentado ante USCIS.",
      decision: "Su caso está en la etapa de decisión."
    },
    allCaughtUp: "Está todo al día. Le avisaremos cuando necesitemos algo."
  },
  tasks: {
    title: "Lo que necesitamos de usted",
    count_one: "1 tarea",
    count_other: "{{count}} tareas",
    none: "Nada por ahora",
    noneBody: "Cuando su equipo legal necesite algo de usted, aparecerá aquí.",
    progress: "{{answered}} de {{total}} respondidas",
    minutesLeft_one: "falta aproximadamente 1 minuto",
    minutesLeft_other: "faltan aproximadamente {{count}} minutos",
    due: "vence el {{date}}",
    dueToday: "vence hoy",
    overdue: "venció el {{date}}",
    changesRequested: "Su equipo legal le pidió actualizar algunas respuestas",
    start: "Empezar",
    continue: "Continuar",
    update: "Actualizar respuestas",
    opening: "Abriendo…",
    openFailed:
      "No pudimos abrir esta tarea. Use el enlace de su correo o inténtelo de nuevo.",
    previewOnly: "Las tareas solo se abren para el cliente.",
    sign: "Revisar y firmar",
    ink: "Empezar",
    notYet: "Todavía no",
    signMeta: "Unos {{minutes}} minutos",
    inkMeta:
      "Imprimir, firmar con tinta y enviar una foto · unos {{minutes}} minutos",
    upcoming: "Se abre cuando esté listo para su firma",
    sendAgain: "Su equipo legal le pidió que lo envíe de nuevo"
  },
  case: {
    title: "Su caso",
    titlePlural: "Sus casos",
    fullTimeline: "Cronología completa",
    now: "Qué está pasando ahora",
    onHold:
      "Su caso está en pausa por ahora. Su equipo legal le avisará cuando continúe.",
    approved: "Aprobado",
    decision: "Decisión tomada",
    receipts: "Recibos de USCIS",
    noReceipts: "Su número de recibo aparecerá aquí cuando USCIS lo envíe.",
    checkStatus: "Consultar estado en USCIS",
    checking: "Consultando…",
    checkedAt: "Consultado {{when}}",
    statusFailed: "USCIS no responde en este momento. Inténtelo más tarde.",
    details: "Detalles del caso",
    caseType: "Tipo de caso",
    opened: "Abierto",
    priorityDate: "Fecha de prioridad",
    updates: "Novedades",
    noUpdates: "Las novedades sobre su caso aparecerán aquí.",
    none: "Todavía no hay casos",
    noneBody: "Cuando su equipo legal abra su caso, podrá seguirlo desde aquí.",
    back: "Todos los casos",
    notFound: "No encontramos este caso.",
    step: "Paso {{n}} de {{total}}",
    startedOn: "comenzó el {{date}}",
    nowChip: "Ahora",
    waitingOnYou: "Esperando su respuesta",
    open: "Abrir",
    attorney: "Abogado",
    receiptNumber: "Número de recibo",
    receiptLater: "Aparecerá aquí después de la presentación",
    priorityLater: "Se fija cuando USCIS lo recibe",
    questions: "¿Preguntas sobre su caso?",
    emailLead: "Escribir a {{name}}"
  },
  milestone: {
    rfe: "USCIS pidió más pruebas (una Solicitud de Evidencia, RFE). {{lead}} está preparando la respuesta y le avisará si necesita algo de usted.",
    interview:
      "USCIS programó una entrevista. {{lead}} le ayudará a prepararse y le dirá qué llevar.",
    biometrics:
      "USCIS está en la etapa de datos biométricos: huellas y una foto en una oficina de USCIS. Lleve su aviso de cita y una identificación con foto."
  },
  stageAbout: {
    started: "Su equipo legal abre su caso y define lo que necesita de usted.",
    gathering:
      "Reunimos sus respuestas y documentos: quién es usted, su historial y las pruebas que su caso necesita.",
    review:
      "Su abogado revisa todo, prepara sus formularios y cartas, y puede hacerle algunas preguntas adicionales.",
    filed:
      "Enviamos su caso a USCIS. El número de recibo aparecerá aquí cuando USCIS lo envíe, y podrá consultar su estado desde esta página.",
    decision:
      "USCIS aprueba el caso, pide más pruebas (una Solicitud de Evidencia) o lo niega. Le explicaremos qué sigue."
  },
  stage: {
    started: "Primeros pasos",
    gathering: "Reunión de información",
    review: "Revisión del abogado",
    filed: "Presentado ante USCIS",
    decision: "Decisión"
  },
  explainer: {
    yourTeam: "Su equipo legal",
    yourTeamLower: "su equipo legal",
    started:
      "{{lead}} está preparando su caso. Todo lo que necesitemos de usted aparecerá en esta página.",
    gathering:
      "{{lead}} está reuniendo sus respuestas y documentos. Cuando estén completos, {{leadFirst}} revisará todo y empezará a preparar su solicitud.",
    review:
      "{{lead}} está revisando su información y preparando su solicitud. Es posible que reciba algunas preguntas adicionales.",
    filed:
      "Su caso fue presentado ante USCIS. Por lo general, USCIS primero envía por correo un aviso de recibo y después puede programar una cita de datos biométricos. Los tiempos de trámite varían según el tipo de caso.",
    decision:
      "USCIS tomó una decisión sobre su caso. {{lead}} le explicará qué significa y cuáles son los próximos pasos."
  },
  updates: {
    opened: "Se abrió su caso",
    filed: "Su caso fue presentado ante USCIS",
    approved: "USCIS aprobó su caso",
    decided: "USCIS tomó una decisión sobre su caso",
    reopened: "Se reabrió su caso",
    receipt: "Se agregó el recibo de USCIS {{receipt}}",
    receipt_unnamed: "Se agregó un recibo de USCIS",
    questions_sent: "Le enviamos preguntas para responder",
    answers_received: "Recibimos sus respuestas",
    changes_requested: "Le pedimos actualizar algunas respuestas",
    answers_reviewed: "Se revisaron sus respuestas"
  },
  comingUp: {
    title: "Próximamente",
    taskDue: "Vence: {{title}}",
    allDay: "Todo el día"
  },
  team: {
    title: "Su equipo legal",
    email: "Escribir a {{name}}"
  },
  reminders: {
    title: "Tenga en cuenta",
    expiresIn_one: "{{subject}} vence mañana",
    expiresIn_other: "{{subject}} vence en {{count}} días",
    expiresToday: "{{subject}} vence hoy",
    expired: "{{subject}}: la fecha de vencimiento registrada era el {{date}}",
    expiresHint:
      "Avise a su equipo legal antes de renovarlo o de planear un viaje.",
    expiredHint:
      "Si recibió un documento más reciente, súbalo para que su equipo legal lo tenga.",
    yours: {
      status: "su estatus {{status}}",
      immigration_status_expiration: "su estatus migratorio",
      i94_expiration: "su I-94",
      passport_expiration: "su pasaporte",
      ead_expiration: "su permiso de trabajo (EAD)",
      advance_parole_expiration: "su permiso de viaje (advance parole)",
      visa_expiration: "su visa"
    },
    theirs: {
      immigration_status_expiration: "el estatus migratorio de {{name}}",
      i94_expiration: "el I-94 de {{name}}",
      passport_expiration: "el pasaporte de {{name}}",
      ead_expiration: "el permiso de trabajo (EAD) de {{name}}",
      advance_parole_expiration:
        "el permiso de viaje (advance parole) de {{name}}",
      visa_expiration: "la visa de {{name}}"
    }
  },
  documents: {
    title: "Documentos",
    subtitle: "Lo que nos envió y lo que su equipo legal compartió con usted.",
    upload: "Subir un documento",
    takePhoto: "Tomar una foto",
    uploading: "Subiendo {{name}}…",
    uploaded: "{{name}} se envió a su equipo legal",
    uploadFailed: "{{name}} no se pudo subir. Inténtelo de nuevo.",
    fromYou: "Enviado por usted",
    fromTeam: "De su equipo legal",
    open: "Abrir",
    remove: "Eliminar",
    removed: "Se eliminó {{name}}",
    removeFailed: "No se pudo eliminar. Inténtelo de nuevo.",
    none: "Todavía no hay documentos",
    noneBody:
      "Suba los documentos que le pida su equipo legal, o vuelva más tarde para ver los archivos que compartan con usted.",
    previewOnly: "En la vista previa no se pueden subir archivos.",
    checking: "Revisando {{name}}…",
    looksGood: "{{name}} se ve bien",
    weRead: "Esto es lo que leímos:",
    mayBeHard: "{{name}} puede ser difícil de leer",
    replace: "Reemplazar",
    keep: "Conservar"
  },
  sign: {
    from: "De {{name}}",
    inkWhy:
      "USCIS exige una firma a mano en los formularios en papel, así que esta página no se puede firmar en pantalla.",
    read: "Lea el documento",
    readHint: "Lea todas las páginas antes de firmar.",
    add: "Agregue su firma",
    draw: "Dibujar",
    type: "Escribir",
    drawHint: "Firme aquí con el dedo o el mouse",
    clear: "Borrar",
    fullName: "Su nombre completo",
    consent:
      "Acepto firmar este documento electrónicamente y que mi firma electrónica vale lo mismo que mi firma a mano.",
    signAndSend: "Firmar y enviar a {{name}}",
    signing: "Firmando…",
    audit:
      "Le enviamos por correo una copia firmada. Se registran su firma, la hora y una huella del documento para poder verificarlo después.",
    needSignature: "Primero dibuje o escriba su firma.",
    needConsent: "Marque la casilla para aceptar la firma electrónica.",
    failed: "No se pudo firmar. Inténtelo de nuevo.",
    signed: "Firmado",
    signedBody: "Lo firmó el {{date}}. Le enviamos una copia por correo.",
    signedNoCopy: "Lo firmó el {{date}}.",
    downloadSigned: "Descargar su copia firmada",
    print: "Imprima la página",
    download: "Descargar la página para firmar (PDF)",
    noPrinter:
      "¿No tiene impresora? Pida a su equipo legal que se la envíe por correo postal.",
    inkSign: "Firme con tinta negra",
    inkBox: "Firme en el recuadro “{{box}}” de la página {{page}}.",
    inkBoxPage: "Firme en el recuadro de firma de la página {{page}}.",
    photo: "Tome una foto de la página firmada",
    photoHint:
      "Colóquela plana, con buena luz y con toda la página a la vista.",
    takePhoto: "Tomar una foto",
    chooseFile: "Elegir un archivo",
    retake: "Volver a tomar",
    checking: "Revisando su foto…",
    found: "Se encontró la firma en el recuadro",
    notFound: "No vimos una firma en el recuadro",
    readable: "Se ve toda la página y se puede leer",
    notReadable: "Parte de la página se lee con dificultad",
    unchecked: "No pudimos revisar esta foto, pero puede enviarla igual.",
    send: "Enviar a {{name}}",
    sendAnyway: "Enviarla de todos modos",
    sending: "Enviando…",
    uploadFailed: "La foto no se subió. Inténtelo de nuevo.",
    keepOriginal:
      "Guarde el original. {{name}} podría pedirle que lo envíe por correo postal.",
    sent: "Enviado a {{name}}",
    sentBody:
      "{{name}} lo revisará y le avisará si necesita algo más. Guarde el original.",
    completed: "Listo",
    completedBody: "{{name}} aceptó su página firmada.",
    returned: "{{name}} le pidió que lo envíe de nuevo:",
    upcoming: "Todavía no está listo",
    upcomingBody: "{{name}} le avisará cuando esté listo para su firma.",
    gone: "Este documento ya no necesita su firma.",
    back: "Volver a las tareas",
    previewOnly: "En la vista previa no se puede firmar.",
    viewer: "Documento para firmar",
    changed:
      "Este documento cambió después de enviarse. Su equipo legal se lo enviará de nuevo."
  },
  profile: {
    subtitle:
      "Esta es la información que va en sus formularios de inmigración. Si algo está mal o cambió, sugiera una corrección y {{name}} la revisará.",
    about: "Sobre usted",
    contact: "Cómo comunicarnos con usted",
    lived: "Dónde ha vivido",
    worked: "Dónde ha trabajado",
    studied: "Sus estudios",
    noneOnFile: "Todavía no hay nada registrado",
    notOnFile: "Sin registrar",
    historyHint: "¿Falta algo o hay algún error aquí?",
    tellUs: "Avisar a {{name}}",
    suggest: "Sugerir una corrección",
    add: "Agregar",
    waiting: "Pendiente de revisión por {{name}}",
    withdraw: "Retirar",
    withdrawn: "Sugerencia retirada",
    updated: "Actualizado",
    kept: "{{name}} mantuvo lo que estaba registrado.",
    keptWhy: "{{name}} mantuvo lo que estaba registrado: {{reason}}",
    note: "¿Algo que {{name}} deba saber?",
    notePlaceholder: "Por ejemplo: mi pasaporte tiene la ortografía correcta.",
    addressPlaceholder: "Si se mudó, díganos cuándo.",
    historyLabel: "¿Qué falta o qué está mal?",
    historyPlaceholder:
      "Por ejemplo: viví en 12 Elm St, Austin, TX, de junio de 2019 a mayo de 2020.",
    yourNotes: "Notas que envió",
    attach: "Adjuntar un documento",
    attachHint: "Una foto o un PDF que muestre la información correcta.",
    attaching: "Subiendo {{name}}…",
    attachFailed: "Ese archivo no se pudo subir. Inténtelo de nuevo.",
    removeAttachment: "Quitar el archivo adjunto",
    cancel: "Cancelar",
    send: "Enviar a {{name}}",
    sending: "Enviando…",
    sent: "Enviado a {{name}} para revisión",
    failed: "No se pudo enviar. Inténtelo de nuevo.",
    unchanged: "Eso es lo que ya tenemos. Cambie primero algún dato.",
    previewOnly: "En la vista previa no se pueden enviar sugerencias.",
    present: "actualidad",
    topic: {
      legal_name: "Nombre legal",
      other_names: "Otros nombres que ha usado",
      birthday: "Fecha de nacimiento",
      birthplace: "Lugar de nacimiento",
      citizenships: "Ciudadanía",
      marital_status: "Estado civil",
      email: "Correo electrónico",
      phone: "Teléfono",
      physical_address: "Domicilio",
      mailing_address: "Dirección postal",
      history: "Su historial"
    },
    field: {
      first_name: "Nombre",
      middle_name: "Segundo nombre",
      last_name: "Apellidos",
      other_names: "Otros nombres, separados por comas",
      birthday: "Fecha de nacimiento",
      city_of_birth: "Ciudad",
      state_of_birth: "Estado o provincia",
      country_of_birth: "País",
      citizenships: "Países, separados por comas",
      marital_status: "Estado civil",
      email: "Correo electrónico",
      phone: "Teléfono",
      street: "Calle y número",
      city: "Ciudad",
      state: "Estado",
      zip_code: "Código postal",
      country: "País"
    },
    marital: {
      single: "Soltero/a, nunca casado/a",
      married: "Casado/a",
      divorced: "Divorciado/a",
      widowed: "Viudo/a",
      separated: "Separado/a legalmente",
      annulled: "Matrimonio anulado"
    }
  },
  signIn: {
    title: "Inicie sesión en su portal",
    sentTitle: "Revise su correo electrónico",
    email: "Correo electrónico",
    send: "Enviarme un enlace para iniciar sesión",
    sending: "Enviando…",
    hint: "No necesita contraseña. Le enviaremos a este correo un enlace para iniciar sesión.",
    sent: "Si {{email}} tiene un portal de cliente, le enviamos un enlace para iniciar sesión. Funciona una sola vez y vence en una hora.",
    sendAnother: "Usar otro correo",
    failed: "No se pudo enviar el enlace. Inténtelo de nuevo en un minuto.",
    staff: "¿Trabaja en un bufete?",
    staffLink: "Inicie sesión aquí"
  },
  welcome: {
    signingIn: "Iniciando sesión…",
    expired: "Este enlace venció",
    used: "Este enlace ya se usó",
    invalid: "Este enlace no es válido",
    revoked: "Este enlace ya no funciona",
    off: "El acceso al portal está desactivado",
    offBody: "Pida a su equipo legal que lo vuelva a activar.",
    getNew:
      "Escriba su correo y le enviaremos un nuevo enlace para iniciar sesión."
  },
  intake: {
    language: "Idioma",
    showEnglish: "Ver en inglés",
    showTranslated: "Ver en {{language}}",
    translating: "Traduciendo las preguntas…",
    translationFailed:
      "No pudimos traducir las preguntas en este momento, así que se muestran en inglés.",
    answerHint:
      "Si puede, responda en inglés. Si no, responda en su idioma y su equipo legal lo traducirá.",
    defaultNote:
      "Tómese su tiempo para completar este cuestionario. Asegúrese de que toda la información coincida con sus documentos oficiales.",
    defaultTitle: "Cuestionario de inmigración",
    start: "Empezar",
    resume: "Continuar",
    fix_one: "Corregir {{count}} respuesta →",
    fix_other: "Corregir {{count}} respuestas →",
    takes_one: "Toma aproximadamente {{count}} minuto",
    takes_other: "Toma aproximadamente {{count}} minutos",
    left_one: "Falta aproximadamente {{count}} minuto",
    left_other: "Faltan aproximadamente {{count}} minutos",
    about_one: "Aproximadamente {{count}} minuto",
    about_other: "Aproximadamente {{count}} minutos",
    othersSaved_one: " · la otra respuesta está guardada",
    othersSaved_other: " · las otras {{count}} respuestas están guardadas",
    needsChanges_one: "Su abogado revisó sus respuestas y necesita un cambio.",
    needsChanges_other:
      "Su abogado revisó sus respuestas y necesita {{count}} cambios.",
    attorney: "Abogado",
    yourAttorney: "Su abogado",
    yourAttorneyLower: "su abogado",
    yourFirm: "Su bufete",
    you: "Usted",
    welcomeBack: "Bienvenido de nuevo",
    welcomeBackName: "Bienvenido de nuevo, {{name}}.",
    resumeBody: "Ya empezó este cuestionario. ¿Quiere continuar donde lo dejó?",
    continueWhereLeft: "Continuar donde lo dejé",
    reviewFromStart: "Revisar desde el principio",
    ok: "OK",
    skip: "Omitir",
    continue: "Continuar",
    submit: "Enviar",
    sendTo: "Enviar a {{name}}",
    yes: "Sí",
    no: "No",
    previousStep: "Paso anterior",
    nextStep: "Paso siguiente",
    displaySettings: "Opciones de visualización",
    typeAnswer: "Escriba su respuesta aquí...",
    yourAnswer: "Su respuesta",
    typeOrSelect: "Escriba o elija una opción",
    noMatches: "No hay opciones que coincidan",
    street: "Dirección",
    city: "Ciudad",
    state: "Estado / Provincia",
    statePlaceholder: "Estado",
    postal: "Código postal",
    postalPlaceholder: "Código postal",
    country: "País",
    dragDrop: "Arrastre y suelte sus archivos",
    uploadingShort: "Subiendo...",
    upload: "Subir",
    uploadMore: "Subir más",
    uploadedFiles: "Archivos subidos",
    uploadingDocument: "Subiendo documento",
    uploaded: "Subido",
    uploadedBody_one: "Su archivo se subió correctamente.",
    uploadedBody_other: "Se subieron {{count}} archivos correctamente.",
    someFailed: "Algunos archivos no se pudieron subir",
    uploadFailed: "No se pudo subir",
    pleaseTryAgain: "Inténtelo de nuevo.",
    saved: "Guardado",
    savedBody:
      "Puede cerrar esta página y volver más tarde con el mismo enlace.",
    couldntSave: "No se pudo guardar",
    submitted: "Enviado",
    submittedBody: "Sus respuestas se enviaron correctamente.",
    couldntSubmit: "No se pudo enviar",
    thankYou: "¡Gracias!",
    thankYouBody:
      "Su equipo legal recibió sus respuestas y las revisará pronto. Ya puede cerrar esta pestaña.",
    review: "Revise sus respuestas",
    reviewBody:
      "Revise sus respuestas antes de enviarlas. Puede corregir lo que necesite cambiar.",
    reviewGroupBody:
      "Puede editar o eliminar cualquier entrada, o agregar otra.",
    edit: "Editar",
    remove: "Eliminar",
    notAnswered: "Sin responder",
    notProvided: "No indicado",
    noEntries: "No hay entradas.",
    noEntriesAdded: "No se agregaron entradas.",
    noEntriesYet: "Todavía no hay entradas.",
    noEntriesSubmitted: "No se enviaron entradas.",
    noDetails: "Todavía no hay detalles.",
    noDocChanges: "No se pidieron cambios en los documentos.",
    noDocs: "No hay documentos disponibles.",
    leaveComment: "Deje un comentario...",
    addComment: "Agregar un comentario",
    addReply: "Responder",
    cancel: "Cancelar",
    youSent: "Usted envió",
    youAnswered: "Usted respondió:",
    addAnother: "¿Agregar otro?",
    loadFailed: "No se pudo cargar el cuestionario",
    oops: "Uy, algo salió mal.",
    persists: "Si el problema continúa, comuníquese con su bufete.",
    tryAgain: "Intentar de nuevo",
    attention_one: "Un archivo necesita atención",
    attention_other: "{{count}} archivos necesitan atención",
    attentionBody: "Es posible que su abogado le pida enviarlos de nuevo.",
    sendAnyway: "Enviar de todos modos",
    savesAuto: "Su progreso se guarda automáticamente",
    prefilledCount_one: "{{count}} respuesta ya completada",
    prefilledCount_other: "{{count}} respuestas ya completadas",
    progressOf: "{{current}} de {{total}}",
    savedAt: "Guardado {{time}}",
    backToPortal: "Volver a su portal",
    outline: {
      questions_one: "{{count}} pregunta",
      questions_other: "{{count}} preguntas",
      filled_one: "{{count}} completada",
      filled_other: "{{count}} completadas",
      minutes_one: "{{count}} min",
      minutes_other: "{{count}} min",
      done: "Listo"
    },
    prefill: {
      ask: "¿Es correcto?",
      yes: "Sí, es correcto",
      change: "Cambiarlo",
      fromDocs: "De sus documentos",
      onFile: "Ya lo tenemos registrado"
    },
    date: {
      day: "Día",
      month: "Mes",
      year: "Año",
      pickMonth: "Elija un mes",
      echo: "Usted escribió: {{date}}",
      invalid: "Esa fecha no existe. Revise el día y el mes."
    },
    ph: {
      email: "nombre@ejemplo.com",
      phone: "+1 555 123 4567",
      givenName: "Como aparece en su pasaporte",
      familyName: "Como aparece en su pasaporte",
      middleName: "Déjelo en blanco si no tiene",
      otherNames: "Otros nombres que haya usado",
      fullName: "Nombre completo",
      number: "Tal como aparece en el documento",
      city: "Ciudad",
      country: "País",
      employer: "Empresa u organización",
      jobTitle: "Su cargo",
      school: "Escuela o universidad",
      website: "https://",
      longText: "Escriba su respuesta aquí…",
      answer: "Escriba su respuesta aquí…"
    },
    group: {
      tellUs: "Cuéntenos sobre: {{name}}.",
      tellUsAddresses: "Cuéntenos sobre su historial de domicilios.",
      tellUsEmployment: "Cuéntenos sobre su historial laboral.",
      tellUsStatuses: "Cuéntenos sobre su historial migratorio.",
      tellUsTrips: "Cuéntenos sobre sus entradas y salidas de Estados Unidos.",
      tellUsDependents: "Cuéntenos sobre sus dependientes.",
      leadIn: "¿Tiene algo que agregar en: {{name}}?",
      leadInAddresses: "¿Tiene domicilios anteriores que agregar?",
      leadInEmployment: "¿Tiene empleos anteriores que agregar?",
      leadInStatuses: "¿Tiene estatus migratorios anteriores que agregar?",
      leadInTrips: "¿Tiene entradas o salidas de EE. UU. que agregar?",
      addAnother: "¿Agregar otro: {{name}}?",
      addAnotherAddress: "¿Agregar otro domicilio?",
      addAnotherEmployer: "¿Agregar otro empleador?",
      addAnotherStatus: "¿Agregar otro estatus?",
      addAnotherTrip: "¿Agregar otro viaje?",
      review: "Revise: {{name}}",
      reviewAddresses: "Revise sus domicilios",
      reviewEmployers: "Revise sus empleadores",
      reviewStatuses: "Revise sus estatus",
      reviewTrips: "Revise sus viajes",
      entries: "entradas"
    }
  },
  common: {
    loading: "Cargando…",
    today: "hoy",
    yesterday: "ayer",
    daysAgo_one: "hace 1 día",
    daysAgo_other: "hace {{count}} días"
  }
};
